import { verifySignedResponse } from './sign.js';
const decoder = new TextDecoder();
/** Error thrown when a CumulusVPN API returns an `{status:'error'}` envelope. */
export class ApiError extends Error {
    /** HTTP status as a string, e.g. `"429"`. */
    code;
    /** Error name, fixed to `"ApiError"`. */
    name = 'ApiError';
    /** Slug from the error body (duplicate of the wire `name`). */
    slug;
    constructor(data) {
        super(data.message);
        this.code = data.code;
        this.slug = data.name;
    }
}
/**
 * Fetch a signed CumulusVPN control-API endpoint, verifying the Ed25519
 * signature over the exact response bytes.
 *
 * Success bodies are verified; error bodies (which the gateway does not sign)
 * are surfaced as an {@link ApiError}. When `pinnedSignPubKey` is provided the
 * signature must verify against it; otherwise the header-advertised key is used
 * (trust-on-first-use).
 *
 * A gateway that is slow or unreachable is bounded by `timeoutMs` (default 12s)
 * via an `AbortController`, so callers fail cleanly instead of hanging forever
 * (which showed up as an endless "Connecting…" in the clients).
 *
 * @throws {ApiError} If the envelope status is `error`.
 * @throws {Error} If the request times out, or a success body fails verification.
 */
export async function fetchSigned(url, fetchImpl, init, pinnedSignPubKey, timeoutMs = 12_000) {
    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), timeoutMs);
    try {
        const res = await fetchImpl(url, { ...init, signal: controller.signal });
        const bytes = new Uint8Array(await res.arrayBuffer());
        const parsed = JSON.parse(decoder.decode(bytes));
        if (parsed.status === 'error') {
            throw new ApiError(parsed.data);
        }
        const headerPub = res.headers.get('X-CVPN-Sign-PubKey');
        const signature = res.headers.get('X-CVPN-Signature');
        const signPubKey = pinnedSignPubKey ?? headerPub;
        let verified = false;
        if (signature && signPubKey) {
            verified = verifySignedResponse(bytes, signature, signPubKey);
        }
        if (!verified) {
            throw new Error(`fetchSigned: unverified signed response from ${url}`);
        }
        return { data: parsed.data, signPubKey, verified };
    }
    catch (err) {
        if (controller.signal.aborted) {
            throw new Error(`gateway ${url} timed out after ${timeoutMs / 1000}s`);
        }
        throw err;
    }
    finally {
        clearTimeout(timer);
    }
}
//# sourceMappingURL=http.js.map